import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import {
	collectEts1SessionCodes,
	Ets1SessionColumnVM,
	Ets1SessionConfigItem,
	Ets1SessionTreeItem,
	hasEts1RadioChildren,
} from "./ets1-session-layout.utils";
import { clearSessionRemarkWhenUnchecked, wireEts1RadioGroupExclusion } from "./ets1-session-validation.utils";

/** AOCs with eTS-1 v2 session catalogs (FSD §3.4.1.3 FR-FC-003). */
export const ETS1_AOC_CODES = ["maa", "aax", "taa", "tax", "iaa", "paa", "aac"] as const;

export type Ets1AocCode = (typeof ETS1_AOC_CODES)[number];

/** True when the form uses the AOC session catalog and structured reminder notes. */
export function isEts1SessionV2(formDetails?: { ets1SessionVersion?: number | null } | null): boolean {
	return Number(formDetails?.ets1SessionVersion ?? 1) === 2;
}

/** Resolve catalog AOC from form payload; falls back to signed-in user AOC. */
export function resolveEts1SessionAocCode(
	source?: { aoc?: string | { code?: string }; aocCode?: string } | null,
	fallbackAocCode?: string | null,
): string {
	const explicit = source?.aocCode?.trim();
	if (explicit) return explicit.toLowerCase();

	const aoc = source?.aoc;
	if (typeof aoc === "string" && aoc.trim()) return aoc.trim().toLowerCase();
	if (aoc && typeof aoc === "object" && typeof aoc.code === "string" && aoc.code.trim()) {
		return aoc.code.trim().toLowerCase();
	}

	return (fallbackAocCode ?? "").trim().toLowerCase();
}

export function syncEts1SessionFormControls(
	fb: UntypedFormBuilder,
	sessions: UntypedFormGroup,
	sessionRemarks: UntypedFormGroup,
	columns: Ets1SessionColumnVM[],
	subs: Array<{ unsubscribe: () => void }> = [],
): void {
	const codes = collectEts1SessionCodes(columns);
	const existing = new Set(Object.keys(sessions.controls));

	for (const code of codes) {
		if (!sessions.get(code)) {
			sessions.addControl(code, new UntypedFormControl(false), { emitEvent: false });
		}
		if (!sessionRemarks.get(code)) {
			sessionRemarks.addControl(code, new UntypedFormControl(""), { emitEvent: false });
		}
		clearSessionRemarkWhenUnchecked(sessions, sessionRemarks, code, subs);
	}

	for (const code of existing) {
		if (!codes.includes(code)) {
			sessions.removeControl(code, { emitEvent: false });
			sessionRemarks.removeControl(code, { emitEvent: false });
		}
	}

	for (const column of columns) {
		for (const item of column.items) {
			if (!hasEts1RadioChildren(item)) continue;
			wireEts1RadioGroupExclusion(
				item.code,
				item.children.map((child) => child.code),
				sessions,
				subs,
			);
		}
	}

	sessions.updateValueAndValidity({ emitEvent: false });
}

function applyEts1TemplateSelectionToTree(
	sessions: UntypedFormGroup,
	selected: Set<string>,
	items: Ets1SessionTreeItem[],
): void {
	for (const item of items) {
		const childCodes = item.children.map((child) => child.code);
		const selectedChildren = childCodes.filter((code) => selected.has(code));

		if (!childCodes.length) {
			if (selected.has(item.code)) {
				sessions.get(item.code)?.setValue(true, { emitEvent: false });
			}
			continue;
		}

		if (selectedChildren.length > 0) {
			const activeRadioChild = hasEts1RadioChildren(item) ? selectedChildren[0] : null;
			for (const child of item.children) {
				const isSelected = activeRadioChild ? child.code === activeRadioChild : selected.has(child.code);
				sessions.get(child.code)?.setValue(isSelected, { emitEvent: false });
			}
			if (item.selectionMode === "checkbox") {
				sessions.get(item.code)?.setValue(true, { emitEvent: false });
			}
		} else if (selected.has(item.code)) {
			sessions.get(item.code)?.setValue(true, { emitEvent: false });
		}
	}
}

/** Pre-check sessions (and remarks) saved on the admin form template. */
export function patchEts1SessionSelectionFromTemplate(
	sessions: UntypedFormGroup,
	sessionRemarks: UntypedFormGroup,
	selectedCodes: string[],
	remarksByCode: Record<string, string> = {},
	columns: Ets1SessionColumnVM[] = [],
	remarkMaxLength = 100,
): void {
	const selected = new Set(selectedCodes ?? []);

	if (columns.length) {
		for (const column of columns) {
			applyEts1TemplateSelectionToTree(sessions, selected, column.items);
		}
	} else {
		for (const code of selected) {
			sessions.get(code)?.setValue(true, { emitEvent: false });
		}
	}

	for (const [code, remark] of Object.entries(remarksByCode ?? {})) {
		if (!selected.has(code)) continue;
		const trimmed = remark?.toString().trim();
		if (!trimmed) continue;
		sessionRemarks.get(code)?.setValue(trimmed.slice(0, remarkMaxLength), { emitEvent: false });
	}

	propagateEts1ParentRemarksFromChildren(sessionRemarks, columns, selected, remarkMaxLength);
	sessions.updateValueAndValidity({ emitEvent: false });
}

function propagateEts1ParentRemarksFromChildren(
	sessionRemarks: UntypedFormGroup,
	columns: Ets1SessionColumnVM[],
	selected: Set<string>,
	remarkMaxLength: number,
): void {
	const walk = (items: Ets1SessionTreeItem[]) => {
		for (const item of items) {
			if (hasEts1RadioChildren(item) && item.requiresText) {
				const parentCtrl = sessionRemarks.get(item.code);
				const parentRemark = (parentCtrl?.value ?? "").toString().trim();
				if (!parentRemark) {
					for (const child of item.children) {
						if (!selected.has(child.code)) continue;
						const childRemark = (sessionRemarks.get(child.code)?.value ?? "").toString().trim();
						if (childRemark) {
							parentCtrl?.setValue(childRemark.slice(0, remarkMaxLength), { emitEvent: false });
							break;
						}
					}
				}
			}
			if (item.children.length) walk(item.children);
		}
	};

	for (const column of columns) {
		walk(column.items);
	}
}

export function mapApiRowsToEts1SessionConfig(
	rows: Array<{
		code: string;
		description: string;
		requiresText?: boolean;
		columnIndex?: number;
		displayOrder?: number;
		parentCode?: string | null;
		selectionMode?: string;
	}>,
): Ets1SessionConfigItem[] {
	return rows.map((row) => ({
		code: row.code,
		description: row.description,
		requiresText: !!row.requiresText,
		columnIndex: row.columnIndex ?? 0,
		displayOrder: row.displayOrder ?? 0,
		parentCode: row.parentCode ?? null,
		selectionMode: (row.selectionMode as Ets1SessionConfigItem["selectionMode"]) ?? "checkbox",
	}));
}
